import { Amplify } from "aws-amplify";
import { useCallback } from "react";
import { generateClient } from "aws-amplify/api";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { ChatSession, ChatInput, ChatAgentInput, FetchMessagesResult } from "types";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

// // Helper function to offset current time by seconds and return ISO string
// export function offsetCurrentTime(seconds: number): Date {
//   const now = new Date();
//   now.setSeconds(now.getSeconds() + seconds);
//   return now;
// }

// Custom hook for chat operations
const useChatOperations = (tokenid: string | undefined, userId: string) => {
  // Fetch last message for a session
  const getLastMessage = useCallback(
    async (sessionId: string) => {
      try {
        const result: any =
          await client.models.Message.listMessageBySessionIdAndCreatedAt(
            {
              sessionId,
            },
            {
              limit: 1,
              sortDirection: "DESC",
              authMode: "userPool",
              authToken: tokenid,
            }
          );

        if (result.errors && result.errors.length > 0) {
          console.error("GraphQL errors fetching last message:", result.errors);
          return "";
        }

      const lastMessage =
        JSON.parse(result.data[0]?.content ?? "{}").message || "";
      // const lastMessage =
      //   result.data[0]?.content || "";
        
      return lastMessage;
    } catch (error) {
      console.error("Error fetching last message:", error);
      return "";
    }
  }, [tokenid]);

  // Create user last seen entries for all users in a session
  const createUserLastSeen = useCallback(
    async (sessionId: string, users: string[], timestamps: Date | string) => {
      try {
        for (const user of users) {
          const result: any = await client.models.UserLastSeen.create(
            {
              chatSessionId: sessionId,
              timestamps:
                timestamps instanceof Date
                  ? timestamps.toISOString()
                  : timestamps,
              user: user,
              users: users,
            },
            {
              authMode: "userPool",
              authToken: tokenid,
            }
          );

          if (result.errors && result.errors.length > 0) {
            console.error(
              `GraphQL errors creating UserLastSeen for user ${user}:`,
              result.errors
            );
          }
        }
      } catch (error) {
        console.error(
          `Error creating UserLastSeen for session ${sessionId}:`,
          error
        );
      }
    },
    [tokenid]
  );

  // Create a new chat session
  const createChatSession = useCallback(
    async (users: string[], id?: string): Promise<ChatSession> => {
      try {
        const input: { id?: string; users: string[] } = id
          ? { id, users }
          : { users };
        const result: any = await client.models.ChatSession.create(input, {
          authMode: "userPool",
          authToken: tokenid,
        });

        if (result.errors && result.errors.length > 0) {
          console.error("GraphQL errors creating chat session:", result.errors);
          throw new Error("Failed to create chat session");
        }

        const newSession = result.data;
        await createUserLastSeen(
          newSession.id,
          newSession.users,
          newSession.createdAt
        );

        return newSession;
      } catch (error) {
        console.error("Error creating chat session:", error);
        throw error;
      }
    },
    [createUserLastSeen, tokenid]
  );

  // Fetch message IDs by session ID
  const fetchMessageIDsBySessionId = useCallback(
    async (
      sessionId: string,
      nextToken: string | null
    ): Promise<FetchMessagesResult> => {
      try {
        const result: any =
          await client.models.Message.listMessageBySessionIdAndCreatedAt(
            {
              sessionId,
            },
            {
              nextToken: nextToken,
              limit: 25,
              sortDirection: "DESC",
              authMode: "userPool",
              authToken: tokenid,
            }
          );

        if (result.errors && result.errors.length > 0) {
          console.error("GraphQL errors:", result.errors);
          return { items: [], nextToken: null };
        }

        return {
          items: result.data.map((message: { id: string }) => message.id),
          nextToken: result.nextToken,
        };
      } catch (error) {
        console.error("Error fetching message IDs:", error);
        return { items: [], nextToken: null };
      }
    },
    [tokenid]
  );

  // Delete a message
  const deleteMessage = useCallback(
    async (messageId: string) => {
      try {
        const result: any = await client.models.Message.delete(
          {
            id: messageId,
          },
          {
            authMode: "userPool",
            authToken: tokenid,
          }
        );

        if (result.errors && result.errors.length > 0) {
          console.error(
            `GraphQL errors deleting message ${messageId}:`,
            result.errors
          );
        }
      } catch (error) {
        console.error(`Error deleting message ${messageId}:`, error);
      }
    },
    [tokenid]
  );

  // Delete a message
  const batchDeleteMessage = useCallback(
    async (messagesToDelete: string[]) => {
      try {
        const result: any = await client.mutations.BatchDeleteMessages(
          {
            messagesToDelete: messagesToDelete,
          },
          {
            authMode: "userPool",
            authToken: tokenid,
          }
        );

        if (result.errors && result.errors.length > 0) {
          console.error(
            `GraphQL errors batch deleting messages:`,
            result.errors
          );
        }
      } catch (error) {
        console.error(`Error deleting message:`, error);
      }
    },
    [tokenid]
  );

  // Delete a chat session
  const deleteChatSession = useCallback(
    async (sessionId: string) => {
      try {
        const result: any = await client.models.ChatSession.delete(
          {
            id: sessionId,
          },
          {
            authMode: "userPool",
            authToken: tokenid,
          }
        );

        if (result.errors && result.errors.length > 0) {
          console.error(
            `GraphQL errors deleting session ${sessionId}:`,
            result.errors
          );
        }
      } catch (error) {
        console.error(`Error deleting session ${sessionId}:`, error);
      }
    },
    [tokenid]
  );

  // Delete user last seen entries for all users in a session
  const deleteUserLastSeen = useCallback(
    async (sessionId: string, users: string[]) => {
      try {
        for (const user of users) {
          const result: any = await client.models.UserLastSeen.delete(
            {
              chatSessionId: sessionId,
              user: user,
            },
            {
              authMode: "userPool",
              authToken: tokenid,
            }
          );

          if (result.errors && result.errors.length > 0) {
            console.error(
              `GraphQL errors deleting UserLastSeen for user ${user}:`,
              result.errors
            );
          }
        }
      } catch (error) {
        console.error(
          `Error deleting UserLastSeen for session ${sessionId}:`,
          error
        );
      }
    },
    [tokenid]
  );

  // Send agent message
  const sendAgent = useCallback(
    async (input: ChatAgentInput) => {
      try {
        if (userId === "Unknown") {
          throw new Error("User is not signed in");
        }
        console.log("Sending agent message:", input)
        const result: any = await client.mutations.sendAgent(
          {
            input,
          },
          {
            authMode: "userPool",
            authToken: tokenid,
          }
        );

        if (result.errors && result.errors.length > 0) {
          console.error("GraphQL errors sending agent message:", result.errors);
          return null;
        }

        return result;
      } catch (error) {
        console.error("Error sending agent message:", error);
        return null;
      }
    },
    [tokenid, userId]
  );

  // Send normal message
  const sendMessage = useCallback(
    async (input: ChatInput) => {
      try {
        if (userId === "Unknown") {
          throw new Error("User is not signed in");
        }
        const result: any = await client.models.Message.create(input, {
          authMode: "userPool",
          authToken: tokenid,
        });

        if (result.errors && result.errors.length > 0) {
          console.error("GraphQL errors sending message:", result.errors);
          return null;
        }

        return result;
      } catch (error) {
        console.error("Error sending normal message:", error);
        return null;
      }
    },
    [tokenid, userId]
  );

  // Update user last seen timestamp
  const updateUserLastSeen = useCallback(
    async (sessionId: string, users: string[]) => {
      try {
        if (userId === "Unknown") {
          throw new Error("User is not signed in");
        }
        // const currentTimestamp = offsetCurrentTime(-0); // offset for delays
        const currentTimestamp =  new Date();
        const result: any = await client.models.UserLastSeen.update(
          {
            chatSessionId: sessionId,
            timestamps: currentTimestamp.toISOString(),
            user: userId,
          },
          {
            authMode: "userPool",
            authToken: tokenid,
          }
        );

        if (result.errors && result.errors.length > 0) {
          console.error("GraphQL errors updating LastSeen:", result.errors);
          return null;
        }

        return currentTimestamp;
      } catch (error) {
        console.error("Error updating LastSeen:", error);
        return null;
      }
    },
    [tokenid, userId]
  );

  // Fetch messages by session ID
  const fetchMessagesBySessionId = useCallback(
    async (
      sessionId: string,
      nextToken: string | null,
      setLoadingState: (state: boolean) => void,
      setChatMessages: (fn: (prev: any[]) => any[]) => void,
      setNextToken: (token: string | null) => void
    ) => {
      try {
        setLoadingState(true);
        const result: any =
          await client.models.Message.listMessageBySessionIdAndCreatedAt(
            {
              sessionId,
            },
            {
              nextToken: nextToken,
              limit: 8,
              sortDirection: "DESC",
              authMode: "userPool",
              authToken: tokenid,
            }
          );

        if (result.errors && result.errors.length > 0) {
          console.error("GraphQL errors fetching messages:", result.errors);
          return;
        }

        setChatMessages((prevMessages) => [...prevMessages, ...result.data]);
        setNextToken(result.nextToken);
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoadingState(false);
      }
    },
    [tokenid]
  );

  // Fetch chat sessions
  const fetchChatSessions = useCallback(
    async (
      setChatSessions: (sessions: ChatSession[]) => void,
      updateChatSessionsWithUnreadCount: (
        sessions: ChatSession[]
      ) => Promise<void>
    ) => {
      let allItems: any[] = [];
      let nextToken: string | null = null;

      try {
        if (userId === "Unknown") {
          throw new Error("User is not signed in");
        }

        do {
          const result: any =
            await client.models.UserLastSeen.listUserLastSeenByUserAndTimestamps(
              {
                user: userId,
              },
              {
                nextToken: nextToken,
                limit: 100,
                sortDirection: "DESC",
                authMode: "userPool",
                authToken: tokenid,
              }
            );
          console.log("listUserLastSeenByUserAndTimestamps: ", result);

          if (result.errors && result.errors.length > 0) {
            console.error(
              "GraphQL errors fetching chat sessions:",
              result.errors
            );
            break;
          }

          allItems = allItems.concat(result.data);

          nextToken = result.nextToken;
        } while (nextToken);
        console.log("allItems: ", allItems);
        const mappedItems: ChatSession[] = await Promise.all(
          allItems.map(async (item: any) => ({
            id: item.chatSessionId,
            users: item.users,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            lastMessage: await getLastMessage(item.chatSessionId),
            userLastSeen: item.timestamps,
            unreadCount: 0,
          }))
        );

        updateChatSessionsWithUnreadCount(mappedItems);
      } catch (error) {
        console.error("Error fetching chat sessions:", error);
      }
    },
    [getLastMessage, tokenid, userId]
  );

  // Delete session and all associated messages
  const deleteSessionAndMessages = useCallback(
    async (
      sessionId: string,
      users: string[],
      setChatMessages: (messages: any[]) => void,
      setChatSessions: (fn: (prev: ChatSession[]) => ChatSession[]) => void,
      setCurrentChatSession: (session: null) => void
    ) => {
      try {
        if (userId === "Unknown") {
          throw new Error("User is not signed in");
        }

        // Clear selected session and messages on the interface first
        // Give user the illusion that deleion is already done
        await deleteChatSession(sessionId);
        await deleteUserLastSeen(sessionId, users);

        setChatSessions((prevSessions) =>
          prevSessions.filter((session) => session.id !== sessionId)
        );
        setCurrentChatSession(null);
        setChatMessages([]);

        let nextToken: string | null = null;

        try {
          do {
            const result: any = await fetchMessageIDsBySessionId(
              sessionId,
              nextToken
            );
            const messageIDs = result.items;
            nextToken = result.nextToken;
            // const deletePromises = messageIDs.map((messageID: string) =>
            //   deleteMessage(messageID)
            // );
            const resultdel: any = await batchDeleteMessage(messageIDs);
            console.log("deleting messages response: ", resultdel);
          } while (nextToken);
        } catch (error) {
          console.error("Error in deleting Messages:", error);
        }
      } catch (error) {
        console.error("Error deleting session and messages:", error);
      }
    },
    [
      deleteChatSession,
      batchDeleteMessage,
      deleteUserLastSeen,
      fetchMessageIDsBySessionId,
      userId,
    ]
  );

  // Calculate total unread count
  const calculateTotalUnreadCount = useCallback(
    (
      chatSessions: ChatSession[],
      currentSessionId: string | undefined
    ): number => {
      return chatSessions.reduce((total, session) => {
        if (session.id === currentSessionId) {
          return total; // Skip current session
        }
        return total + (session.unreadCount || 0);
      }, 0);
    },
    []
  );

  // Update chat sessions with unread count
  const updateChatSessionsWithUnreadCount = useCallback(
    async (
      chatSessions: ChatSession[],
      currentSessionId: string | undefined,
      setChatSessions: (sessions: ChatSession[]) => void
    ) => {
      try {
        if (userId === "Unknown") {
          throw new Error("User is not signed in");
        }

        const updatedChatSessions = await Promise.all(
          chatSessions.map(async (session) => {
            const { id: sessionId, userLastSeen } = session;

            if (sessionId === currentSessionId) {
              return { ...session, unreadCount: 0 };
            }

            const lastSeenTimestamp = userLastSeen || new Date(0);

            let nextToken: string | null = null;
            let unreadCount = 0;

            try {
              do {
                const result: any =
                  await client.models.Message.listMessageBySessionIdAndCreatedAt(
                    {
                      sessionId: sessionId,
                      createdAt: {
                        gt:
                          lastSeenTimestamp instanceof Date
                            ? lastSeenTimestamp.toISOString()
                            : lastSeenTimestamp,
                      },
                    },
                    {
                      nextToken: nextToken,
                      authMode: "userPool",
                      authToken: tokenid,
                    }
                  );

                if (result.errors && result.errors.length > 0) {
                  console.error(
                    `GraphQL errors fetching unread messages for session ${sessionId}:`,
                    result.errors
                  );
                  break;
                }

                const messages = result.data;
                unreadCount += messages.length;
                nextToken = result.nextToken;
              } while (nextToken);

              return { ...session, unreadCount };
            } catch (error) {
              console.error(
                `Error in fetching messages for session ${sessionId}:`,
                error
              );
              return { ...session, unreadCount: 0 };
            }
          })
        );
        setChatSessions(updatedChatSessions);
      } catch (error) {
        console.error(
          "Error updating chat sessions with unread counts:",
          error
        );
      }
    },
    [tokenid, userId]
  );

  return {
    getLastMessage,
    createUserLastSeen,
    createChatSession,
    fetchMessageIDsBySessionId,
    deleteMessage,
    deleteChatSession,
    deleteUserLastSeen,
    sendAgent,
    sendMessage,
    updateUserLastSeen,
    fetchMessagesBySessionId,
    fetchChatSessions,
    deleteSessionAndMessages,
    calculateTotalUnreadCount,
    updateChatSessionsWithUnreadCount,
  };
};

export default useChatOperations;
